import { Feature, FeatureCollection } from 'geojson';
import { HotelMapDetailsModalProps } from '@/components/modules/hotel-map/hotel-map-details-modal/hotel-map-details-modal';
import { TagVariant } from '@/components/molecules/tag/tag';

export function isMapHotelEntry(
  item: Content.Properties.HotelMapItem,
): item is Content.Properties.MapHotelEntry {
  return item.$type === 'MapHotelEntry';
}

export function isMapPoiEntry(
  item: Content.Properties.HotelMapItem,
): item is Content.Properties.MapPoiEntry {
  return item.$type === 'MapPoiEntry';
}

export const hotelMapDetailsCollectionAdapter = (
  item?: Content.Properties.MapHotelEntry,
): Partial<HotelMapDetailsModalProps> => {
  if (!item) return {};

  const tags = [
    ...item.facilityHighlights.map(value => ({
      text: value,
      variant: 'primary' as TagVariant,
    })),
    ...item.distances.map(value => ({
      text: value,
      variant: 'secondary' as TagVariant,
    })),
  ];

  return {
    description: item.description,
    image: item.picture
      ? {
          altText: item.picture.description ?? '',
          url: item.picture.url,
        }
      : undefined,
    name: item.hotelName,
    rating: item.stars ?? undefined,
    tags,
    totalPricePerPerson: item.bestPrice?.totalPricePerPerson ?? undefined,
    type: item.hotelType,
    url: item.url,
  };
};

/**
 * Map hotel items
 */
export const extractMapHotelItems = (
  items: Content.Properties.HotelMapItem[],
): Content.Properties.MapHotelEntry[] => {
  const result: Content.Properties.MapHotelEntry[] = [];

  for (const item of items) {
    if (isMapHotelEntry(item)) {
      result.push(item);
    }
  }

  return result;
};

export const mapHotelItemsFeatureAdapter = (
  item: Content.Properties.MapHotelEntry,
): Feature => {
  return {
    geometry: {
      coordinates: [
        item.geoLocation?.longitude ?? 0,
        item.geoLocation?.latitude ?? 0,
      ],
      type: 'Point',
    },
    properties: {
      id: String(item.hotelId),
      markerLabel: item.hotelName,
      name: item.hotelName,
      type: item.pointOfInterestType,
    },
    type: 'Feature',
  };
};

export const mapHotelItemsFeatureCollectionAdapter = (
  items?: Content.Properties.MapHotelEntry[],
): FeatureCollection => {
  if (!items) {
    return {
      features: [],
      type: 'FeatureCollection',
    };
  }

  return {
    features: items.map(item => mapHotelItemsFeatureAdapter(item)),
    type: 'FeatureCollection',
  };
};

/**
 * Map poi items
 */
export const extractMapPoiItems = (
  items: Content.Properties.HotelMapItem[],
): Content.Properties.MapPoiEntry[] => {
  const result: Content.Properties.MapPoiEntry[] = [];

  for (const item of items) {
    if (isMapPoiEntry(item)) {
      result.push(item);
    }
  }

  return result;
};

export const mapPoiItemsFeatureAdapter = (
  item: Content.Properties.MapPoiEntry,
): Feature => {
  return {
    geometry: {
      coordinates: [
        item.geoLocation?.longitude ?? 0,
        item.geoLocation?.latitude ?? 0,
      ],
      type: 'Point',
    },
    properties: {
      filterLabel: String(item.filterLabel),
      id: String(item.id),
      name: item.name,
      poiLabel: item.name,
      type: item.pointOfInterestType,
    },
    type: 'Feature',
  };
};

export const mapPoiItemsFeatureCollectionAdapter = (
  items?: Content.Properties.MapPoiEntry[],
): FeatureCollection => {
  if (!items) {
    return {
      features: [],
      type: 'FeatureCollection',
    };
  }

  return {
    features: items.map(item => mapPoiItemsFeatureAdapter(item)),
    type: 'FeatureCollection',
  };
};
